import type { StatusTone } from '@/modules/settings';
import { isAfterTrigger, type RuleOutcome, type RuleStatus, type RunRecord, type RunStatus } from '@/types/rules.types';

/** What each playbook status is called on screen; "shadow" is "Watching", which says what it does. */
export const RULE_STATUS_META: Record<RuleStatus, { label: string; tone: StatusTone; help: string }> = {
	live: { label: 'Live', tone: 'green', help: 'Runs every time its trigger happens, and acts.' },
	shadow: { label: 'Watching', tone: 'blue', help: 'Runs and records what it would do, without changing anything.' },
	draft: { label: 'Draft', tone: 'grey', help: 'Never runs. Test it, then switch it on.' },
	paused: { label: 'Paused', tone: 'amber', help: 'Switched off. It keeps its steps for when you switch it back on.' }
};

/** What happened to the message, past tense for the log and conditional for a test. */
export const OUTCOME_RESULT: Record<RuleOutcome, { done: string; wouldDo: string; tone: StatusTone }> = {
	hand_to_kim: { done: 'Sent to Advanced AI', wouldDo: 'Would go to Advanced AI', tone: 'green' },
	hand_to_team: { done: 'Handed to your team', wouldDo: 'Would go to your team', tone: 'amber' },
	stop: { done: 'Stopped', wouldDo: 'Would stop here', tone: 'grey' }
};

/** On Advanced AI's answer the same decisions say what happened to the reply. */
const ANSWER_RESULT: Partial<Record<RuleOutcome, { done: string; wouldDo: string; tone: StatusTone }>> = {
	hand_to_kim: { done: 'Reply went as usual', wouldDo: 'Reply would go as usual', tone: 'green' },
	hand_to_team: { done: 'Reply held for your team', wouldDo: 'Reply would be held for your team', tone: 'amber' }
};

/** After something happened there is no decision: a run either did its actions or had nothing to do. */
const AFTER_RESULT = {
	acted: { done: 'Actions done', wouldDo: 'Would do its actions', tone: 'green' },
	idle: { done: 'Nothing to do', wouldDo: 'Nothing to do', tone: 'grey' }
} as const;

const TO_SOCIAL_AGENT = { done: 'Sent to the Social Media Agent', wouldDo: 'Would go to the Social Media Agent', tone: 'green' } as const;

/** How a run ended, in its trigger's words. An "after" run is about its actions, not a decision. */
export const runResult = (run: Pick<RunRecord, 'outcome' | 'trigger' | 'actions'>) => {
	if (isAfterTrigger(run.trigger)) return AFTER_RESULT[run.actions.length > 0 ? 'acted' : 'idle'];
	// A comment that goes on is the Social Media Agent's: it never reaches Advanced AI.
	if (run.trigger === 'social_comment' && run.outcome === 'hand_to_kim') return TO_SOCIAL_AGENT;
	return (run.trigger === 'ai_answer' ? ANSWER_RESULT[run.outcome] : undefined) ?? OUTCOME_RESULT[run.outcome];
};

/** Who settled the run: the deciding playbook, or for an "after" run the first one that matched. */
export const runDecider = (run: Pick<RunRecord, 'trigger' | 'decidedBy' | 'rules'>): string | null =>
	isAfterTrigger(run.trigger) ? (run.rules.find((trace) => trace.matched)?.name ?? null) : (run.decidedBy?.ruleName ?? null);

export const RUN_STATUS_META: Record<RunStatus, { label: string; tone: StatusTone }> = {
	completed: { label: 'Completed', tone: 'green' },
	failed: { label: 'Failed', tone: 'red' },
	fallback: { label: 'Fell back', tone: 'amber' }
};

/** The left border and icon tint of each step kind (If blue, action violet, outcome by outcome). */
export const STEP_ACCENT = {
	if: { border: 'border-l-blue-500', text: 'text-blue-500', bg: 'bg-blue-100' },
	action: { border: 'border-l-violet-500', text: 'text-violet-500', bg: 'bg-violet-100' },
	hand_to_kim: { border: 'border-l-green-500', text: 'text-green-500', bg: 'bg-green-100' },
	hand_to_team: { border: 'border-l-amber-500', text: 'text-amber-500', bg: 'bg-amber-50' },
	stop: { border: 'border-l-red-500', text: 'text-red-500', bg: 'bg-red-50' }
} as const;

/** "0.9 s", or "12 ms" for the very quick ones. */
export const formatMs = (ms: number): string => (ms < 100 ? `${Math.round(ms)} ms` : `${(ms / 1000).toFixed(1)} s`);

/** "Matched 12 times this week". */
export function matchedText(count: number | undefined): string {
	if (!count) return 'Not matched this week';
	return count === 1 ? 'Matched once this week' : `Matched ${count.toLocaleString()} times this week`;
}
